import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PrimeArtifact } from '../../types/primeArtifact';
import { EnhancedArtifact } from '../../types/artifacts';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import {
  PrimeToCompanyImportRequest,
  PrimeToCompanyImportService,
} from '../../services/primeToCompanyImportService';
import styles from './PrimeToCompanyImportDialog.module.css';

interface PrimeToCompanyImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  companyArtifacts: EnhancedArtifact[];
  selectedCompanyArtifact: EnhancedArtifact | null;
  companyId?: string;
}

interface PrimeArtifactSelection {
  primeArtifact: PrimeArtifact;
  companyName: string;
  selected: boolean;
}

interface ImportNode {
  id: string;
  companyArtifact: EnhancedArtifact;
  primeSelections: PrimeArtifactSelection[];
  children: ImportNode[];
}

export const PrimeToCompanyImportDialog: React.FC<PrimeToCompanyImportDialogProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  companyArtifacts,
  companyId,
}) => {
  const [primeArtifacts, setPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [primeHierarchy, setPrimeHierarchy] = useState<PrimeArtifact[]>([]);
  const [importTree, setImportTree] = useState<ImportNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<ImportNode | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const primeArtifactMap = useMemo(() => {
    const map = new Map<string, PrimeArtifact>();
    const queue = [...primeHierarchy];
    while (queue.length) {
      const current = queue.shift();
      if (!current) continue;
      map.set(current.id, current);
      if (current.children?.length) {
        queue.push(...current.children);
      }
    }
    return map;
  }, [primeHierarchy]);

  const flattenPrimeArtifacts = useCallback((nodes: PrimeArtifact[]): PrimeArtifact[] => {
    const results: PrimeArtifact[] = [];
    const stack = [...nodes];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      results.push(node);
      if (node.children?.length) {
        stack.push(...node.children);
      }
    }
    return results;
  }, []);

  const getAncestorIds = useCallback(
    (primeId: string): Set<string> => {
      const ancestors = new Set<string>();
      let current = primeArtifactMap.get(primeId);
      while (current?.parent_ref) {
        const parent = primeArtifactMap.get(current.parent_ref);
        if (!parent) break;
        ancestors.add(parent.id);
        current = parent;
      }
      return ancestors;
    },
    [primeArtifactMap]
  );

  // Load prime artifacts on mount
  useEffect(() => {
    if (isOpen) {
      void loadPrimeArtifacts();
    }
  }, [isOpen]);

  const loadPrimeArtifacts = async () => {
    try {
      setIsLoading(true);
      const hierarchy = await PrimeArtifactService.getArtifactsHierarchy();
      setPrimeHierarchy(hierarchy);
      setPrimeArtifacts(flattenPrimeArtifacts(hierarchy));
    } catch (err) {
      setError('Failed to load prime artifacts');
      console.error('Error loading prime artifacts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const buildImportTree = useCallback(() => {
    if (!companyArtifacts.length || !primeArtifacts.length) {
      setImportTree([]);
      setSelectedNode(null);
      return;
    }

    const defaultPrimeSelections = primeArtifacts.map((prime) => ({
      primeArtifact: prime,
      companyName: prime.name, // Default name
      selected: false,
    }));

    // Build tree structure from company artifacts
    const buildNode = (artifact: EnhancedArtifact): ImportNode => {
      const children = companyArtifacts
        .filter((child) => child.parent_ref === artifact.id)
        .map(buildNode);

      return {
        id: artifact.id,
        companyArtifact: artifact,
        primeSelections: defaultPrimeSelections.map((selection) => ({ ...selection })),
        children,
      };
    };

    // Find root artifacts (those without parent_ref)
    const rootArtifacts = companyArtifacts.filter((artifact) => !artifact.parent_ref);
    const tree = rootArtifacts.map(buildNode);

    setImportTree(tree);
    if (tree.length > 0) {
      setSelectedNode(tree[0]);
    }
  }, [companyArtifacts, primeArtifacts]);

  useEffect(() => {
    if (isOpen) {
      buildImportTree();
    }
  }, [isOpen, companyArtifacts, primeArtifacts, buildImportTree]);

  const handleNodeSelect = (node: ImportNode) => {
    setSelectedNode(node);
  };

  const handlePrimeSelectionChange = (primeId: string, selected: boolean) => {
    if (!selectedNode) return;

    const ancestorIds = getAncestorIds(primeId);

    const updatedNode = {
      ...selectedNode,
      primeSelections: selectedNode.primeSelections.map((selection) => {
        if (selection.primeArtifact.id === primeId) {
          return { ...selection, selected };
        }
        if (selected && ancestorIds.has(selection.primeArtifact.id)) {
          return { ...selection, selected: true };
        }
        return selection;
      }),
    };

    // Update the tree
    const updateTree = (nodes: ImportNode[]): ImportNode[] => {
      return nodes.map((node) => {
        if (node.id === selectedNode.id) {
          return updatedNode;
        }
        return {
          ...node,
          children: updateTree(node.children),
        };
      });
    };

    setImportTree(updateTree(importTree));
    setSelectedNode(updatedNode);
  };

  const handleCompanyNameChange = (primeId: string, name: string) => {
    if (!selectedNode) return;

    const updatedNode = {
      ...selectedNode,
      primeSelections: selectedNode.primeSelections.map((selection) =>
        selection.primeArtifact.id === primeId ? { ...selection, companyName: name } : selection
      ),
    };

    // Update the tree
    const updateTree = (nodes: ImportNode[]): ImportNode[] => {
      return nodes.map((node) => {
        if (node.id === selectedNode.id) {
          return updatedNode;
        }
        return {
          ...node,
          children: updateTree(node.children),
        };
      });
    };

    setImportTree(updateTree(importTree));
    setSelectedNode(updatedNode);
  };

  const handleImport = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const primeArtifactIds = getSelectedPrimeIds();
      if (primeArtifactIds.length === 0) {
        setError('Select at least one prime artifact to import.');
        return;
      }

      // Create a map of existing company artifacts by prime_artifact_id
      // This helps us skip importing artifacts that already exist and find existing parents
      const existingCompanyArtifactsByPrimeId = new Map<string, EnhancedArtifact>();
      companyArtifacts.forEach((artifact) => {
        if (artifact.prime_artifact_id) {
          existingCompanyArtifactsByPrimeId.set(artifact.prime_artifact_id, artifact);
        }
      });

      // Collect all selected prime artifacts with their target company artifacts
      interface PrimeImportInfo {
        primeArtifact: PrimeArtifact;
        companyName: string;
        targetCompanyArtifactId: string;
      }
      
      const primeImports: PrimeImportInfo[] = [];
      
      // First pass: collect all selected prime artifacts, but skip if they already exist
      const collectSelections = (nodes: ImportNode[]) => {
        nodes.forEach((node) => {
          node.primeSelections
            .filter((selection) => selection.selected)
            .forEach((selection) => {
              // Skip if this prime artifact already exists in the company model
              const existing = existingCompanyArtifactsByPrimeId.get(selection.primeArtifact.id);
              if (existing) {
                console.log(`[PrimeToCompanyImportDialog] Skipping ${selection.primeArtifact.name} - already exists as ${existing.name}`);
                return; // Skip this artifact
              }
              
              primeImports.push({
                primeArtifact: selection.primeArtifact,
                companyName: selection.companyName,
                targetCompanyArtifactId: node.companyArtifact.id,
              });
            });
          if (node.children.length > 0) {
            collectSelections(node.children);
          }
        });
      };
      collectSelections(importTree);

      // If no artifacts to import after filtering, show error
      if (primeImports.length === 0) {
        setError('All selected prime artifacts already exist in the company model. No new artifacts to import.');
        setIsLoading(false);
        return;
      }

      // Create a map: prime_id -> PrimeImportInfo (for artifacts being imported)
      const primeImportMap = new Map<string, PrimeImportInfo>();
      primeImports.forEach((info) => {
        primeImportMap.set(info.primeArtifact.id, info);
      });

      // Sort prime artifacts by depth (parents before children) to ensure correct order
      const getPrimeDepth = (primeId: string): number => {
        const prime = primeArtifactMap.get(primeId);
        if (!prime || !prime.parent_ref) return 0;
        return 1 + getPrimeDepth(prime.parent_ref);
      };

      const sortedPrimeImports = [...primeImports].sort((a, b) => {
        return getPrimeDepth(a.primeArtifact.id) - getPrimeDepth(b.primeArtifact.id);
      });

      // Build import nodes: one per prime artifact with correct parent_id
      const importNodes = sortedPrimeImports.map((primeImport) => {
        const { primeArtifact, companyName, targetCompanyArtifactId } = primeImport;
        const primeParentId = primeArtifact.parent_ref;

        // Determine the parent company artifact ID for this new artifact
        let parentCompanyArtifactId: string | undefined = undefined;

        if (primeParentId) {
          // First, check if the parent already exists in the company model
          const existingParent = existingCompanyArtifactsByPrimeId.get(primeParentId);
          if (existingParent) {
            // Parent already exists - use its ID as parent
            parentCompanyArtifactId = existingParent.id;
          } else {
            // Parent doesn't exist - check if it's being imported
            const parentImport = primeImportMap.get(primeParentId);
            if (parentImport) {
              // Parent is being imported in this batch
              // Since we process parents before children (sorted by depth), the parent will be created first
              // However, the backend doesn't track created artifacts to resolve parent_id references
              // So we need to use a workaround: pass the target company artifact ID where parent will be created
              // The backend will use: parent_ref = parent_id or target_artifact.id
              // This means the child will incorrectly have parent_ref = target_artifact.id
              // TODO: Backend needs to track created artifacts and resolve parent_id to newly created artifacts
              // For now, we'll use the target, but this is a known limitation
              parentCompanyArtifactId = parentImport.targetCompanyArtifactId;
            } else {
              // Parent is not being imported and doesn't exist
              // This shouldn't happen if the hierarchy is complete, but handle it gracefully
              // New artifact should be a child of the target company artifact
              const targetArtifact = companyArtifacts.find((a) => a.id === targetCompanyArtifactId);
              parentCompanyArtifactId = targetArtifact?.id || targetCompanyArtifactId;
            }
          }
        } else {
          // Prime artifact has no parent (root)
          // If the root already exists, we shouldn't be importing it (filtered above)
          // So this should be a new root, which means parent_id should be null
          // But we're importing it as a child of the target, so use target's parent_ref
          const targetArtifact = companyArtifacts.find((a) => a.id === targetCompanyArtifactId);
          // If target is root (no parent_ref), new artifact should also be root
          // Otherwise, new artifact is child of target
          parentCompanyArtifactId = targetArtifact?.parent_ref || undefined;
        }

        return {
          company_artifact_id: targetCompanyArtifactId,
          parent_id: parentCompanyArtifactId,
          prime_selections: [
            {
              prime_artifact_id: primeArtifact.id,
              company_name: companyName,
              selected: true,
            },
          ],
        };
      });

      // Prepare the import request
      const importRequest: PrimeToCompanyImportRequest = {
        company_id: companyId,
        prime_artifact_ids: primeArtifactIds,
        import_nodes: importNodes,
      };

      // Call the import API
      const result = await PrimeToCompanyImportService.importPrimeToCompany(importRequest);

      console.log('Import completed:', result);

      // Check for errors in the response
      const importedCount = result.imported_count ?? result.created_artifacts ?? 0;
      const errors = result.errors ?? [];

      if (errors.length > 0) {
        // Display errors to the user
        const errorMessage = `Import completed with ${errors.length} error(s):\n${errors.join('\n')}`;
        setError(errorMessage);
        console.error('[PrimeToCompanyImportDialog] Import errors:', errors);
        
        // If some artifacts were imported, still refresh the tree
        if (importedCount > 0) {
          console.log(`[PrimeToCompanyImportDialog] ${importedCount} artifacts imported despite errors, refreshing tree`);
          onImportComplete();
        }
        // If no artifacts were imported, don't close the dialog so user can see errors
        return;
      }

      // Check if any artifacts were actually imported
      if (importedCount === 0) {
        setError('No artifacts were imported. Please check your selections and try again.');
        console.warn('[PrimeToCompanyImportDialog] Import completed but no artifacts were created');
        return;
      }

      // Success - close dialog and refresh tree
      console.log(`[PrimeToCompanyImportDialog] Successfully imported ${importedCount} artifacts`);
      onImportComplete();
    } catch (err: any) {
      console.error('[PrimeToCompanyImportDialog] Import error:', err);
      
      // Handle 404 specifically - endpoint might not be deployed
      if (err.response?.status === 404) {
        const errorMessage = 'Import endpoint not found. The server may need to be updated with the latest backend code.';
        setError(errorMessage);
        console.error('[PrimeToCompanyImportDialog] 404 Error - Endpoint /prime-to-company/import not found on server');
      } else {
        const errorMessage = err.response?.data?.detail || 
                            err.response?.data?.message || 
                            (err instanceof Error ? err.message : 'Failed to import prime artifacts');
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderTreeNode = (node: ImportNode, level: number = 0) => (
    <div
      key={node.id}
      className={`${styles.treeNode} level-${level} ${selectedNode?.id === node.id ? styles.selected : ''}`}
      style={{ marginLeft: `${level * 20}px` }}
      onClick={() => handleNodeSelect(node)}
    >
      <div className={styles.nodeHeader}>
        <span className={styles.nodeName}>{node.companyArtifact.name}</span>
        <span className={styles.nodeType}>{node.companyArtifact.artifact_type_id}</span>
        <span className={styles.selectionCount}>
          {node.primeSelections.filter((s) => s.selected).length} selected
        </span>
      </div>
      {node.companyArtifact.description && (
        <div className={styles.nodeDescription}>{node.companyArtifact.description}</div>
      )}

      {node.children.map((child) => renderTreeNode(child, level + 1))}
    </div>
  );

  const getSelectionForPrime = useCallback(
    (primeId: string) => {
      if (!selectedNode) return null;
      return selectedNode.primeSelections.find((selection) => selection.primeArtifact.id === primeId) || null;
    },
    [selectedNode]
  );

  const renderPrimeTree = (nodes: PrimeArtifact[], level = 0) => {
    if (!selectedNode) return null;

    return nodes.map((prime) => {
      const selection = getSelectionForPrime(prime.id);
      const isSelected = selection?.selected ?? false;
      return (
        <div
          key={prime.id}
          className={`${styles.primeSelection} ${isSelected ? styles.selected : ''}`}
          style={{ marginLeft: `${level * 16}px` }}
        >
          <div className={styles.selectionHeader}>
            <label className={styles.selectionCheckbox}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => handlePrimeSelectionChange(prime.id, e.target.checked)}
              />
              <span className={styles.primeName}>{prime.name}</span>
            </label>
          </div>

          {isSelected && selection && (
            <div className={styles.selectionDetails}>
              <div className={styles.nameInput}>
                <label>Company Artifact Name:</label>
                <input
                  type="text"
                  value={selection.companyName}
                  onChange={(e) => handleCompanyNameChange(prime.id, e.target.value)}
                  placeholder={selection.primeArtifact.name}
                />
              </div>
              <div className={styles.primeInfo}>
                <p>
                  <strong>Type:</strong> {selection.primeArtifact.artifact_type_id}
                </p>
                {selection.primeArtifact.description && (
                  <p>
                    <strong>Description:</strong> {selection.primeArtifact.description}
                  </p>
                )}
              </div>
            </div>
          )}

          {prime.children && prime.children.length > 0 && renderPrimeTree(prime.children, level + 1)}
        </div>
      );
    });
  };

  const getSelectedPrimes = (): PrimeArtifactSelection[] => {
    const getAllSelections = (nodes: ImportNode[]): PrimeArtifactSelection[] => {
      let selections: PrimeArtifactSelection[] = [];
      nodes.forEach((node) => {
        selections = selections.concat(node.primeSelections.filter((s) => s.selected));
        selections = selections.concat(getAllSelections(node.children));
      });
      return selections;
    };
    return getAllSelections(importTree);
  };

  const getSelectedPrimeIds = (): string[] => {
    const ids = new Set<string>();
    getSelectedPrimes().forEach((sel) => ids.add(sel.primeArtifact.id));
    return Array.from(ids);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.importDialogOverlay}>
      <div className={`${styles.importDialog} ${styles.primeToCompanyDialog}`}>
        <div className={styles.importDialogHeader}>
          <h2>Import Prime Artifacts to Company</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.importDialogContent}>
          <div className={styles.importLayout}>
            {/* Left side: Company artifact tree */}
            <div className={styles.companyTreePanel}>
              <h3>Company Artifact Structure</h3>
              <div className={styles.treeContainer}>{importTree.map((node) => renderTreeNode(node))}</div>
            </div>

            {/* Right side: Prime artifact selection */}
            <div className={styles.primeSelectionPanel}>
              {selectedNode ? (
                <>
                  <h3>Add Prime Artifacts to: {selectedNode.companyArtifact.name}</h3>
                  <div className={styles.primeSelections}>
                    {primeHierarchy.length > 0 ? (
                      renderPrimeTree(primeHierarchy)
                    ) : (
                      <div className={styles.noSelection}>
                        <p>No prime artifacts available to import.</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className={styles.noSelection}>
                  <p>Select a company artifact to add prime artifacts</p>
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className={styles.importSummary}>
            <h3>Import Summary</h3>
            <div className={styles.summaryStats}>
              <span>Selected Prime Artifacts: {getSelectedPrimes().length}</span>
              <span>
                Target Company Nodes:{' '}
                {importTree.filter((node) => node.primeSelections.some((s) => s.selected)).length}
              </span>
            </div>
          </div>

          {error && (
            <div className={styles.errorMessage} style={{ 
              padding: '12px', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px',
              marginTop: '16px',
              whiteSpace: 'pre-wrap',
              maxHeight: '200px',
              overflow: 'auto'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className={styles.stepActions}>
            <button onClick={onClose} className={styles.secondaryButton}>
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={getSelectedPrimes().length === 0 || isLoading}
              className={styles.primaryButton}
            >
              {isLoading ? 'Importing...' : `Import ${getSelectedPrimes().length} Artifacts`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
